import { UploadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import {
  useCallback,
  useRef,
  useState,
  type ReactNode,
} from "react";
import styled from "styled-components";
import { colors } from "../../../theme/colors";
import { Row } from "../grid/row.component";
import { Pressable } from "../pressable/pressable.component";
import { MdDelete } from "react-icons/md";
import _ from "lodash";
import { IoMdEye } from "react-icons/io";

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept?.trim()) return true;
  const tokens = accept.split(",").map((t) => t.trim().toLowerCase());
  const name = file.name.toLowerCase();
  const mime = (file.type || "").toLowerCase();
  return tokens.some((token) => {
    if (!token) return false;
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) {
      const prefix = token.slice(0, -2);
      return mime.startsWith(`${prefix}/`);
    }
    return (
      mime === token ||
      name.endsWith(token.startsWith(".") ? token : `.${token}`)
    );
  });
}

function formatAcceptHint(accept?: string): string {
  if (!accept?.trim()) return "";
  return accept
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .join(" · ");
}

export const FileUpload = ({
  file,
  onChange,
  accept,
  uploaded,
  fileName,
  fileUrl,
  variant = "button",
  dropzonePreview,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  uploaded: number | boolean | null;
  fileName?: string;
  fileUrl?: string | null;
  variant?: "button" | "dropzone";
  /** When set (e.g. server-encoded player), replaces empty-state UI inside the drop zone. */
  dropzonePreview?: ReactNode;
}) => {
  const input = useRef<HTMLInputElement | null>(null);
  const dragCounter = useRef(0);
  const [dragActive, setDragActive] = useState(false);

  const openPicker = useCallback(() => {
    input.current?.click();
  }, []);

  const resetInput = useCallback(() => {
    try {
      if (!input.current) return;
      input.current.value = "";
      if (input.current.value) {
        input.current.type = "text";
        input.current.type = "file";
      }
    } catch {
      /* noop */
    }
  }, []);

  const handleDropFiles = useCallback(
    (list: FileList | null) => {
      const first = list?.[0];
      if (!first) return;
      if (!matchesAccept(first, accept)) {
        return;
      }
      onChange(first);
      resetInput();
    },
    [accept, onChange, resetInput]
  );

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragActive(false);
      handleDropFiles(e.dataTransfer.files);
    },
    [handleDropFiles]
  );

  const clearFile = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      onChange(null);
      resetInput();
    },
    [onChange, resetInput]
  );

  const previewFile = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      if (fileUrl) {
        window.open(fileUrl);
      } else if (file) {
        const blobUrl = URL.createObjectURL(file);
        window.open(blobUrl);
      }
    },
    [file, fileUrl]
  );

  const onKeyDownDropzone = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPicker();
      }
    },
    [openPicker]
  );

  const hasSelection = !!(file || fileName);
  const inProgress = typeof uploaded === "number" && uploaded < 1;
  const failed = uploaded === false;
  const uploadComplete =
    uploaded === true ||
    uploaded === 1 ||
    (!!fileName && !inProgress && !failed);
  const acceptHint = formatAcceptHint(accept);

  const serverPreviewNode = dropzonePreview != null ? dropzonePreview : null;
  // A local video player is position-absolute, so it gives the box no height
  // and the card collapses to the toolbar after upload. Keep the file name in
  // the drop zone until a real server preview exists.
  const showPreviewLayout = !inProgress && !!serverPreviewNode;

  const previewInner = serverPreviewNode ? (
    <PreviewInnerStopClick>{serverPreviewNode}</PreviewInnerStopClick>
  ) : null;

  const inputEl = (
    <input
      type="file"
      style={{ display: "none" }}
      ref={input}
      {...(accept ? { accept } : {})}
      onChange={(evt) => {
        onChange(evt.target.files?.length ? evt.target.files[0] : null);
        resetInput();
      }}
    />
  );

  if (variant === "dropzone") {
    return (
      <>
        {inputEl}
        <DropZone
          role={showPreviewLayout ? "region" : "button"}
          tabIndex={showPreviewLayout ? -1 : 0}
          aria-label={showPreviewLayout ? "Uploaded media" : "Upload file"}
          $dragActive={dragActive}
          $complete={uploadComplete}
          $failed={failed}
          $previewMode={showPreviewLayout}
          onClick={
            showPreviewLayout
              ? undefined
              : () => {
                  openPicker();
                }
          }
          onKeyDown={showPreviewLayout ? undefined : onKeyDownDropzone}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <DropZoneToolbar>
            <ToolbarBtn type="button" aria-label="Remove file" onClick={clearFile}>
              <MdDelete size={22} />
            </ToolbarBtn>
            {file || fileUrl || serverPreviewNode ? (
              <ToolbarBtn type="button" aria-label="Preview file" onClick={previewFile}>
                <IoMdEye size={22} />
              </ToolbarBtn>
            ) : null}
          </DropZoneToolbar>

          {showPreviewLayout ? (
            <DropZonePreviewBody>{previewInner}</DropZonePreviewBody>
          ) : (
            <DropZoneBody>
              <UploadOutlined style={{ fontSize: "3.2rem", opacity: 0.85 }} />
              <DropZoneTitle>
                {inProgress
                  ? `Uploading ${Math.floor((uploaded as number) * 100)}%`
                  : uploadComplete && hasSelection
                    ? "Uploaded"
                    : dragActive
                      ? "Drop to upload"
                      : "Drag & drop or click to upload"}
              </DropZoneTitle>
              {acceptHint && !inProgress && !(uploadComplete && hasSelection) ? (
                <DropZoneHint>{acceptHint}</DropZoneHint>
              ) : null}
              {inProgress ? (
                <ProgressBlock>
                  <ProgressTrack>
                    <ProgressFill
                      style={{ width: `${Math.max(2, Math.floor((uploaded as number) * 100))}%` }}
                    />
                  </ProgressTrack>
                  <DropZoneFileName title={file?.name ?? fileName ?? "video"}>
                    {file?.name ?? fileName ?? "video"}
                  </DropZoneFileName>
                </ProgressBlock>
              ) : hasSelection ? (
                <DropZoneFileName
                  $success={uploadComplete}
                  title={file?.name ?? fileName ?? ""}
                >
                  {failed ? "Upload failed · " : ""}
                  {!failed && uploadComplete ? "✓ " : ""}
                  {file?.name ?? fileName ?? ""}
                </DropZoneFileName>
              ) : null}
            </DropZoneBody>
          )}
        </DropZone>
      </>
    );
  }

  return (
    <>
      {inputEl}
      <Row style={{ alignItems: "center" }} $gap={10}>
        <Button
          size="large"
          type={"primary"}
          icon={<UploadOutlined />}
          onClick={() => {
            if (input.current) input.current.click();
          }}
        >
          {file || fileName ? (
            <>
              <span
                style={{
                  color:
                    uploaded === true || uploaded === 1 || !!fileName
                      ? colors.success.hex
                      : colors.complement.hex,
                }}
              >
                {uploaded === true || uploaded === 1 || !!fileName
                  ? "Uploaded "
                  : uploaded === false ||
                      (typeof uploaded === "number" && uploaded < 1)
                    ? uploaded === false
                      ? "Upload failed "
                      : "Uploading " +
                        `(${Math.floor((uploaded as number) * 100)}%) `
                    : ""}
              </span>
              "{_.truncate(file?.name ?? fileName, { length: 15 })}"
            </>
          ) : (
            "Upload"
          )}
        </Button>
        <Pressable
          onClick={() => {
            try {
              if (!input.current) return;

              onChange(null);
              input.current.files = null;
              resetInput();
            } catch {
              /* noop */
            }
          }}
        >
          <MdDelete />
        </Pressable>
        {file || fileUrl ? (
          <Pressable
            onClick={() => {
              if (fileUrl) {
                window.open(fileUrl);
              } else if (file) {
                const blobUrl = URL.createObjectURL(file);

                window.open(blobUrl);
              }
            }}
          >
            <IoMdEye />
          </Pressable>
        ) : null}
      </Row>
    </>
  );
};

const DropZone = styled.div<{
  $dragActive: boolean;
  $complete: boolean;
  $failed: boolean;
  $previewMode: boolean;
}>`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-self: stretch;
  flex: 1 1 auto;
  min-height: 14rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: ${({ $previewMode }) => ($previewMode ? "0.85rem" : "2rem 1.6rem")};
  border-radius: 1rem;
  outline: none;
  cursor: ${({ $previewMode }) => ($previewMode ? "default" : "pointer")};
  border: 2px dashed
    ${({ $dragActive, $complete, $failed }) =>
      $failed
        ? colors.error.hex
        : $dragActive
          ? colors.primary.hex
          : $complete
            ? colors.success.hex
            : colors.grey.hex};
  background: ${({ $dragActive, $previewMode }) =>
    $previewMode
      ? colors.textReverse.withOpacity(colors.isDark ? 0.08 : 0.55).hex
      : $dragActive
        ? colors.primary.withOpacity(colors.isDark ? 0.14 : 0.08).hex
        : colors.textReverse.withOpacity(colors.isDark ? 0.06 : 0.5).hex};
  transition: border-color 0.2s ease, background 0.2s ease,
    box-shadow 0.2s ease;

  ${({ $previewMode }) =>
    !$previewMode &&
    `
  &:hover {
    border-color: ${colors.primary.hex};
    background: ${colors.primary.withOpacity(colors.isDark ? 0.12 : 0.06).hex};
    box-shadow: 0 0 0 1px ${colors.primary.withOpacity(0.25).hex};
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${colors.primary.withOpacity(0.35).hex};
  }
  `}
`;

const DropZoneToolbar = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  gap: 0.35rem;
  z-index: 3;
`;

const ToolbarBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem;
  border: none;
  border-radius: 0.55rem;
  background: ${colors.textReverse.withOpacity(colors.isDark ? 0.14 : 0.92).hex};
  color: ${colors.text.hex};
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);

  &:hover {
    background: ${colors.primary.withOpacity(0.28).hex};
    transform: scale(1.05);
  }
`;

const PreviewInnerStopClick = styled.div`
  width: 100%;
  min-width: 0;
  align-self: stretch;
`;

const DropZonePreviewBody = styled.div`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 14rem;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  padding: 2.75rem 0.4rem 0.4rem;
  box-sizing: border-box;
`;

const DropZoneBody = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  text-align: center;
  gap: 0.6rem;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 0 0.8rem;
  color: ${colors.text.hex};

  .anticon {
    color: ${colors.primary.hex};
    opacity: 0.95;
  }
`;

const DropZoneTitle = styled.p`
  margin: 0;
  max-width: 100%;
  font-size: 1.45rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${colors.text.hex};
  overflow-wrap: break-word;
`;

const DropZoneHint = styled.p`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
  opacity: 0.62;
  color: ${colors.text.hex};
`;

const ProgressBlock = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.7rem;
  margin-top: 0.4rem;
  overflow: hidden;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 0.7rem;
  border-radius: 999px;
  overflow: hidden;
  background: ${colors.text.withOpacity(0.15).hex};
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #438dc7 0%, #205496 100%);
  transition: width 0.2s ease;
`;

const DropZoneFileName = styled.p<{ $success?: boolean; $uploading?: boolean }>`
  margin: 0.4rem 0 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 500;
  color: ${({ $success, $uploading }) =>
    $success
      ? colors.success.hex
      : $uploading
        ? colors.complement.hex
        : colors.text.hex};
  opacity: ${({ $uploading }) => ($uploading ? 0.95 : 0.72)};
`;
